/**
 * ClientProgressBar — the simple view (Lloyd, Aug 19 2026).
 *
 * *"I don't know if we need all of these steps to be visible to the client. In the client's portal,
 * can we have a simpler view for them to see?"*
 *
 * Eight stages, plain words, no codes and no dashed boxes. Deliberately not the staff chart with
 * things hidden: this shares no rendering with CaseStatusTimeline, so nothing internal can arrive
 * on a client's screen by someone adding a step to the journey map. The only thing the two share is
 * the stage index, and that is the point — the client is told exactly what the case page says,
 * spelled differently.
 *
 * Read-only by construction. There is no `onSelectStage`, so no future edit can make a client's
 * click move their own case.
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  CLIENT_STAGES,
  CLIENT_STAGE_UNKNOWN,
  clientStage,
} from '../CaseStatusTimeline/clientStages';

interface ClientProgressBarProps {
  /** Index into CLIENT_STAGES / CASE_STAGES. -1 when nothing recognisable is set. */
  currentStageIndex: number;
  /**
   * Traffic or non-traffic. The eight stages describe a traffic case; a county or district criminal
   * matter passes through none of them, so the bar is not shown at all rather than shown wrong.
   */
  matterType?: string | null;
  /** Hides the explanatory sentence, for places that are tight on room. */
  hideDescription?: boolean;
}

function ClientProgressBar({
  currentStageIndex,
  matterType,
  hideDescription = false,
}: ClientProgressBarProps) {
  if (matterType === 'NON_TRAFFIC') return null;

  const stage = clientStage(currentStageIndex) ?? CLIENT_STAGE_UNKNOWN;
  const known = clientStage(currentStageIndex) !== null;

  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
        {CLIENT_STAGES.map((s, index) => {
          const done = known && index < currentStageIndex;
          const isCurrent = known && index === currentStageIndex;

          return (
            <Box key={s.short} sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: isCurrent
                    ? 'primary.main'
                    : done
                      ? 'primary.light'
                      : 'grey.300',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent ? 'text.primary' : 'text.secondary',
                  // The eight labels have to survive a phone screen; wrapping beats truncating,
                  // because a half-word tells the client less than a second line costs them.
                  overflowWrap: 'anywhere',
                }}
              >
                {s.short}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {stage.label}
      </Typography>
      {!hideDescription && (
        <Typography variant="body2" color="text.secondary">
          {stage.description}
        </Typography>
      )}
    </Box>
  );
}

export default ClientProgressBar;
